import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { connectAndMint } from "@/lib/caryConnect";
import { X, KeyRound, Wallet, Loader2, DownloadCloud, Plus, Trash2, RefreshCw, CheckCircle2, AlertTriangle, History, Zap, Wrench, Link as LinkIcon } from "lucide-react";


const SUMMARY_CHIPS = [
  ["mining_days", "mining days"],
  ["community_rows", "community rows"],
  ["staking_positions", "staking positions"],
  ["withdrawals", "withdrawals"],
  ["purchases", "hashrate buys"],
];

function ImportSummary({ s }) {
  if (!s) return null;
  return (
    <div className="mt-2 space-y-1.5" data-testid="cp-import-summary">
      <div className="flex flex-wrap gap-1.5">
        {SUMMARY_CHIPS.map(([k, label]) => (
          <span key={k} className="chip text-[11px]" style={{ background: s[k] ? "rgba(0,134,58,0.08)" : "rgba(15,18,38,0.04)", color: s[k] ? "#00863A" : "#8A8FA8" }}>
            {s[k] ?? 0} {label}
          </span>
        ))}
      </div>
      {(s.errors || []).map((e, i) => (
        <div key={i} className="text-[11px] flex items-start gap-1" style={{ color: "#B45309" }}>
          <AlertTriangle size={11} className="mt-0.5 shrink-0" /> {e}
        </div>
      ))}
    </div>
  );
}

export default function CaryPactHistoryModal({ onClose, onImported }) {
  const [conns, setConns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ label: "", address: "", token: "" });
  const [busyAdd, setBusyAdd] = useState(false);
  const [importing, setImporting] = useState(null);
  const [results, setResults] = useState({});
  const [tokenFor, setTokenFor] = useState(null);
  const [freshToken, setFreshToken] = useState("");
  const [busyToken, setBusyToken] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [batch, setBatch] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/cp-connections");
      setConns(data);
      if (!data.length) setShowAdd(true);
    } catch { /* transient */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Resume watching a batch sync that's already running (e.g. modal was closed mid-run).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/cp-connections/sync-all/status");
        if (data.status === "running") setBatch(data);
      } catch { /* ignore */ }
    })();
  }, []);

  const finishBatch = (data) => {
    const map = {};
    (data.results || []).forEach((r) => { if (r.summary) map[r.conn_id] = r.summary; });
    if (Object.keys(map).length) setResults((prev) => ({ ...prev, ...map }));
    onImported?.();
    load();
    const ok = (data.results || []).filter((r) => r.status === "synced").length;
    const exp = (data.results || []).filter((r) => r.status === "token_expired").length;
    const err = (data.results || []).filter((r) => r.status === "error").length;
    if (data.status === "failed") toast.error(data.error || "Batch sync failed part-way — run it again.");
    else if (exp || err) toast.warning(`Synced ${ok} wallet${ok === 1 ? "" : "s"} — ${exp ? `${exp} need${exp === 1 ? "s" : ""} a re-connect` : ""}${exp && err ? ", " : ""}${err ? `${err} failed` : ""}.`, { duration: 9000 });
    else toast.success(`All ${ok} wallet${ok === 1 ? "" : "s"} fully synced — detail + history up to date. 🎉`, { duration: 7000 });
  };

  useEffect(() => {
    if (batch?.status !== "running") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/cp-connections/sync-all/status");
        setBatch(data);
        if (data.status && data.status !== "running") { clearInterval(t); finishBatch(data); }
      } catch { /* keep polling */ }
    }, 1500);
    return () => clearInterval(t);
  }, [batch?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const startBatch = async () => {
    try {
      const { data } = await api.post("/cp-connections/sync-all/start");
      setBatch({ status: "running", phase: data.already ? "Already running — catching up…" : "Starting…", done: 0, total: data.total ?? conns.length, results: [] });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start the batch sync.");
    }
  };

  // One-tap connect: sign CaryPact's login challenge → mint token (no F12).
  const connectWallet = async (label, reconnectId, expectedAddr) => {
    setConnecting(reconnectId || "add");
    try {
      const data = await connectAndMint(label, expectedAddr || null);
      toast.success(`Wallet connected — token minted for ${data.address.slice(0, 6)}…${data.address.slice(-4)}. No F12 needed! 🎉`, { duration: 7000 });
      setForm({ label: "", address: "", token: "" });
      setShowAdd(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Could not connect the wallet.", { duration: 8000 });
    } finally { setConnecting(false); }
  };

  const addConn = async () => {
    if (!form.token.trim() || !form.address.trim()) { toast.error("Paste the wallet address and its x-auth-token."); return; }
    setBusyAdd(true);
    try {
      const { data } = await api.post("/cp-connections", {
        token: form.token.trim(), address: form.address.trim(), label: form.label.trim() || null,
      });
      toast.success(data.updated ? "Token refreshed for this wallet." : `Wallet connected${data.verified ? " · ownership verified" : ""}.`);
      if (!data.verified && !data.updated) toast.info("Couldn't verify ownership yet (no transfers found) — import will confirm it.", { duration: 7000 });
      setForm({ label: "", address: "", token: "" });
      setShowAdd(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not connect that wallet.");
    } finally { setBusyAdd(false); }
  };

  const runImport = async (c) => {
    setImporting(c.id);
    try {
      const { data } = await api.post(`/cp-connections/${c.id}/import`);
      setResults((r) => ({ ...r, [c.id]: data }));
      if (data.token_expired) {
        toast.warning("Token expired part-way — imported what it could. Paste a fresh token and run again.", { duration: 8000 });
      } else if ((data.errors || []).length) {
        toast.warning("Imported with some warnings — see details below.");
      } else {
        toast.success(`History imported: ${data.mining_days} mining days · ${data.community_rows} community rows · ${data.staking_positions} staking · ${data.withdrawals} withdrawals · ${data.purchases} purchases.`, { duration: 8000 });
      }
      onImported?.();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed.");
    } finally { setImporting(null); }
  };

  const runRebuild = async (c) => {
    if (!window.confirm(`Rebuild "${c.label}" from CaryPact?\n\nThis deletes every IMPORTED ledger row for this wallet (fixing duplicated or corrupt days like a doubled chart spike) and re-imports the full history fresh. Manual entries are untouched. Nothing is deleted unless the token checks out first.`)) return;
    setImporting(c.id);
    try {
      const { data } = await api.post(`/cp-connections/${c.id}/rebuild`);
      setResults((r) => ({ ...r, [c.id]: data }));
      const del = data.deleted || {};
      const wiped = Object.values(del).reduce((a, n) => a + (n || 0), 0);
      if ((data.errors || []).length) {
        toast.warning(`Rebuilt (${wiped} old rows cleared) but with warnings — see details below.`, { duration: 8000 });
      } else {
        toast.success(`Rebuilt clean: ${wiped} old rows cleared → ${data.mining_days} mining days · ${data.community_rows} community rows · ${data.staking_positions} staking · ${data.withdrawals} withdrawals re-imported.`, { duration: 9000 });
      }
      onImported?.();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Rebuild failed — nothing was deleted.");
    } finally { setImporting(null); }
  };

  const saveToken = async (c) => {
    if (!freshToken.trim()) { toast.error("Paste a fresh x-auth-token."); return; }
    setBusyToken(true);
    try {
      await api.post(`/cp-connections/${c.id}/token`, { token: freshToken.trim() });
      toast.success("Token updated.");
      setTokenFor(null); setFreshToken("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save the token.");
    } finally { setBusyToken(false); }
  };

  const removeConn = async (c) => {
    if (!window.confirm(`Remove "${c.label}"? Already-imported history stays in your ledgers.`)) return;
    try {
      await api.delete(`/cp-connections/${c.id}`);
      toast.success("Connection removed — imported data kept.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not remove.");
    }
  };

  const shortAddr = (a) => (a && a.length > 12 ? `${a.slice(0, 6)}..${a.slice(-4)}` : a);
  const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString() : "never");

  return createPortal(
    <div className="fixed inset-0 z-[210] flex items-center justify-center p-4" data-testid="cp-history-modal">
      <div className="absolute inset-0" style={{ background: "rgba(15,18,38,0.40)", backdropFilter: "blur(4px)" }} onClick={onClose} />
      <div className="relative glass w-full max-w-xl p-6 max-h-[92vh] overflow-y-auto" style={{ borderRadius: 20 }}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="kpi-label flex items-center gap-1.5"><History size={12} /> CaryPact history</div>
            <h3 className="font-display text-lg font-semibold tracking-tight">Import your full earning history</h3>
            <p className="text-xs text-ink-3 mt-1 leading-relaxed">
              Pulls day-by-day <b>mining</b>, <b>community</b> and <b>staking</b> rewards plus <b>withdrawals</b> and <b>hashrate purchases</b> straight from CaryPact into your Revenue ledgers. One token per wallet — re-running never duplicates.
            </p>
          </div>
          <button onClick={onClose} className="org-icon-btn shrink-0" title="Close" data-testid="cp-history-close"><X size={16} /></button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-ink-3 py-6 justify-center"><Loader2 size={16} className="animate-spin" /> Loading connections…</div>
        ) : (
          <div className="space-y-3">
            {conns.length > 0 && (
              <div className="rounded-xl p-4" style={{ background: "rgba(124,58,237,0.06)", border: "1px solid rgba(124,58,237,0.20)" }} data-testid="cp-batch-panel">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm flex items-center gap-1.5"><RefreshCw size={13} style={{ color: "#7C3AED" }} /> Sync all wallets</div>
                    <div className="text-[11px] text-ink-3 mt-0.5 leading-relaxed">Goes wallet-by-wallet using each wallet's own token: full CaryPact detail + complete history.</div>
                  </div>
                  <button onClick={startBatch} disabled={batch?.status === "running"} className="btn-primary !py-1.5 !px-3 text-xs shrink-0" data-testid="cp-batch-start">
                    {batch?.status === "running" ? <><Loader2 size={12} className="animate-spin" /> Syncing…</> : <><Zap size={12} /> Sync all now</>}
                  </button>
                </div>
                {batch && batch.status !== "none" && (
                  <div className="mt-3 space-y-2" data-testid="cp-batch-progress">
                    <div className="flex items-center justify-between text-[11px] gap-2">
                      <span className="text-ink-2 font-medium truncate" data-testid="cp-batch-phase">{batch.phase}</span>
                      <span className="text-ink-3 shrink-0" data-testid="cp-batch-count">{Math.min(batch.done ?? 0, batch.total ?? 0)} / {batch.total ?? 0}</span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(15,18,38,0.08)" }}>
                      <div className="h-full rounded-full" style={{ width: `${batch.total ? Math.round(100 * Math.min(batch.done ?? 0, batch.total) / batch.total) : 0}%`, background: "#7C3AED", transition: "width 0.5s ease" }} />
                    </div>
                    {(batch.results || []).map((r) => (
                      <div key={r.conn_id} className="flex items-center gap-2 text-[11px]" data-testid={`cp-batch-result-${r.conn_id}`}>
                        {r.status === "synced"
                          ? <CheckCircle2 size={12} className="shrink-0" style={{ color: "#00863A" }} />
                          : <AlertTriangle size={12} className="shrink-0" style={{ color: r.status === "token_expired" ? "#B45309" : "#DC2626" }} />}
                        <span className="font-medium truncate">{r.label}</span>
                        <span className="text-ink-3 ml-auto shrink-0">
                          {r.status === "synced" ? `synced${r.fields ? ` · ${r.fields} fields` : ""}` : r.status === "token_expired" ? "needs reconnect" : "failed"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {conns.map((c) => {
              const summary = results[c.id] || c.last_import_summary;
              return (
                <div key={c.id} className="rounded-xl p-4" style={{ background: "rgba(15,18,38,0.03)", border: "1px solid rgba(15,18,38,0.07)" }} data-testid={`cp-conn-${c.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm truncate">{c.label}</span>
                        <span className="text-[11px] text-ink-3 font-mono">{shortAddr(c.address)}</span>
                        {c.token_expired ? (
                          <span className="chip text-[10px]" style={{ background: "rgba(245,158,11,0.12)", color: "#B45309" }} data-testid={`cp-conn-expired-${c.id}`}>
                            <AlertTriangle size={10} /> token expired
                          </span>
                        ) : (
                          <span className="chip text-[10px]" style={{ background: "rgba(0,134,58,0.08)", color: "#00863A" }}>
                            <CheckCircle2 size={10} /> connected
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-ink-3 mt-0.5">Last import: {fmtWhen(c.last_import_at)}</div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => runImport(c)} disabled={importing === c.id} className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`cp-conn-import-${c.id}`}>
                        {importing === c.id ? <><Loader2 size={12} className="animate-spin" /> Importing…</> : <><DownloadCloud size={12} /> Import history</>}
                      </button>
                      <button onClick={() => connectWallet(c.label, c.id, c.address)} disabled={connecting === c.id} className="org-icon-btn" title="Reconnect with your wallet (no F12)" data-testid={`cp-conn-connect-${c.id}`} style={{ color: "#7C3AED" }}>
                        {connecting === c.id ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
                      </button>
                      <button onClick={() => { setTokenFor(tokenFor === c.id ? null : c.id); setFreshToken(""); }} className="org-icon-btn" title="Paste a token manually" data-testid={`cp-conn-token-${c.id}`}><RefreshCw size={13} /></button>
                      <button onClick={() => removeConn(c)} className="org-icon-btn" title="Remove connection" data-testid={`cp-conn-remove-${c.id}`}><Trash2 size={13} /></button>
                    </div>
                  </div>
                  {c.token_expired && (
                    <button onClick={() => connectWallet(c.label, c.id, c.address)} disabled={connecting === c.id} className="mt-2 w-full text-xs font-semibold rounded-lg py-2 flex items-center justify-center gap-1.5" style={{ background: "rgba(124,58,237,0.10)", color: "#7C3AED" }} data-testid={`cp-conn-reconnect-${c.id}`}>
                      {connecting === c.id ? <><Loader2 size={12} className="animate-spin" /> Reconnecting…</> : <><Zap size={12} /> Reconnect this wallet (one tap, no F12)</>}
                    </button>
                  )}
                  <button onClick={() => runRebuild(c)} disabled={importing === c.id} className="mt-2 w-full text-xs font-semibold rounded-lg py-2 flex items-center justify-center gap-1.5" style={{ background: "rgba(180,83,9,0.08)", color: "#B45309", border: "1px dashed rgba(180,83,9,0.35)" }} title="Delete all imported rows for this wallet and re-import fresh — fixes duplicated / corrupt days (e.g. a doubled chart spike). Manual entries untouched." data-testid={`cp-conn-rebuild-${c.id}`}>
                    {importing === c.id ? <><Loader2 size={12} className="animate-spin" /> Working…</> : <><Wrench size={12} /> Rebuild imported data (fix duplicated days)</>}
                  </button>
                  {tokenFor === c.id && (
                    <div className="mt-2 flex gap-2">
                      <input value={freshToken} onChange={(e) => setFreshToken(e.target.value)} placeholder="fresh x-auth-token" type="password" className="input-glass flex-1 text-sm font-mono" data-testid={`cp-conn-token-input-${c.id}`} />
                      <button onClick={() => saveToken(c)} disabled={busyToken} className="btn-secondary text-xs" data-testid={`cp-conn-token-save-${c.id}`}>{busyToken ? "Saving…" : "Save"}</button>
                    </div>
                  )}
                  <ImportSummary s={summary} />
                </div>
              );
            })}

            {!conns.length && !showAdd && (
              <div className="text-sm text-ink-3 text-center py-4">No wallets connected yet.</div>
            )}

            {showAdd ? (
              <div className="rounded-xl p-4 space-y-3" style={{ background: "rgba(124,58,237,0.06)", border: "1px solid rgba(124,58,237,0.20)" }} data-testid="cp-add-panel">
                <div className="font-semibold text-sm flex items-center gap-1.5"><Zap size={14} style={{ color: "#7C3AED" }} /> Connect a wallet</div>
                <p className="text-[11px] text-ink-3 leading-relaxed">
                  Open this page inside the <b>TokenPocket browser</b> (or with the TokenPocket / MetaMask extension), tap connect, and approve one signature. We mint your CaryPact token automatically — <b>no F12, no copy-paste</b>.
                </p>
                <label className="block">
                  <span className="kpi-label mb-1 block">Wallet name (optional)</span>
                  <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Main mining wallet" className="input-glass w-full text-sm" data-testid="cp-add-label" />
                </label>
                <button onClick={() => connectWallet(form.label)} disabled={connecting === "add"} className="btn-primary w-full text-sm" data-testid="cp-connect-wallet-btn">
                  {connecting === "add" ? <><Loader2 size={14} className="animate-spin" /> Waiting for your wallet…</> : <><Zap size={14} /> Connect wallet &amp; mint token</>}
                </button>

                <details className="text-[11px] text-ink-3 leading-relaxed rounded-lg p-3" style={{ background: "rgba(15,18,38,0.03)" }} data-testid="cp-manual-paste">
                  <summary className="cursor-pointer font-semibold text-ink-2 flex items-center gap-1.5"><KeyRound size={12} /> Or paste a token manually (advanced)</summary>
                  <div className="mt-3 space-y-2">
                    <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Wallet address 0x…" className="input-glass w-full text-sm font-mono" data-testid="cp-add-address" />
                    <input value={form.token} onChange={(e) => setForm({ ...form, token: e.target.value })} placeholder="x-auth-token" type="password" className="input-glass w-full text-sm font-mono" data-testid="cp-add-token" />
                    <div className="text-ink-3">Get it via F12 → Network → any <b>/api/</b> request → copy the <b>x-auth-token</b> header. A temporary key, never your seed phrase.</div>
                    <button onClick={addConn} disabled={busyAdd} className="btn-secondary w-full text-xs" data-testid="cp-add-submit">
                      {busyAdd ? "Saving…" : "Save pasted token"}
                    </button>
                  </div>
                </details>

                {conns.length > 0 && <button onClick={() => setShowAdd(false)} className="btn-secondary w-full text-sm" data-testid="cp-add-cancel">Cancel</button>}
              </div>
            ) : (
              <button onClick={() => setShowAdd(true)} className="btn-secondary w-full text-sm" data-testid="cp-add-open">
                <Plus size={14} /> Connect another wallet
              </button>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
